import locationHelper from "@emlakjet/commons/helpers/location";
import Locations from "src/utils/constants/locations";
import polyline from "@mapbox/polyline";
import { getNodeLocations } from "../conversion";
import { regionCityIds } from "@/src/components/listing-map/map/constants";
import simplify from "simplify-js";

const simplifyPolygon = (polygon, tolerance = 0.0003) => {
  return simplify(
    polygon.map(([x, y]) => ({ x, y })),
    tolerance,
    true
  ).map((point) => [point.x, point.y]);
};

export const normalizePolygons = (polylinesData) => {
  let data = [];
  let flattenedPolylines = [];
  let decodedPolylines = [];
  if (typeof polylinesData[0][0] === "string" || polylinesData[0][0] instanceof String) {
    flattenedPolylines = polylinesData.flat();
    decodedPolylines = flattenedPolylines.map((flattenedPolyline) => polyline.decode(flattenedPolyline));
  } else {
    decodedPolylines = polylinesData.flat();
  }
  data = decodedPolylines.map((item) => simplifyPolygon(item.map((c) => c.reverse())));
  return data;
};

export const getMapOptions = (locationType) => {
  const options = {
    coordinatesField: "geometryMid.coordinates",
  };

  switch (locationType) {
    case Locations.REGION:
      options.coordinatesField = "geometryMid.coordinates";
      break;
    case Locations.CITY:
      options.coordinatesField = "geometryMid.coordinates";
      break;
    case Locations.DISTRICT:
      options.coordinatesField = "geometryMid.coordinates";
      break;
    case Locations.TOWN:
      options.coordinatesField = "geometryHigh.coordinates";
      break;
    case Locations.LOCALITY:
      options.coordinatesField = "geometryHigh.coordinates";
      break;
    default:
      break;
  }

  return options;
};

export const extractTownsFromLocalities = (locations) => {
  const towns = [];
  const localities = locations.filter((l) => l.type === "locality");
  if (localities.length > 0) {
    localities.forEach((locality) => {
      if (typeof locality.town !== "undefined" && typeof locality.district !== "undefined") {
        locality.town.forEach((localityTown) => {
          localityTown.parentId = locality.district.id;
          localityTown.type = "town";
          towns.push(localityTown);
        });
      }
    });
  }

  return towns;
};

export const isPolygonActive = (location, selectedLocations, activeLocationTypes) => {
  // If any town is selected it will only paint the borders of the town green
  const isTownSelected = selectedLocations.find((selectedLocation) => selectedLocation.type === "town");
  if (isTownSelected) {
    if (activeLocationTypes.indexOf(Locations.TOWN) > -1 && location.type === Locations.TOWN) {
      const selectedLocationIds = selectedLocations.map((s) => s.id);
      if (selectedLocationIds.indexOf(location.id) > -1) {
        return true;
      }
    }
  } else {
    if (activeLocationTypes.indexOf(Locations.LOCALITY) > -1 && location.type === Locations.TOWN) {
      const localityTowns = extractTownsFromLocalities(selectedLocations);
      const selectedLocationIds = localityTowns.map((s) => s.id);
      if (selectedLocationIds.indexOf(location.id) > -1) {
        return true;
      }
    }

    if (activeLocationTypes.indexOf(Locations.DISTRICT) > -1) {
      if (location.type === Locations.TOWN) {
        const selectedLocationIds = selectedLocations.map((s) => s.id);
        if (selectedLocationIds.indexOf(location.district.id) > -1) {
          return true;
        }
      }

      if (location.type === Locations.DISTRICT) {
        const selectedLocationIds = selectedLocations.map((s) => s.id);
        if (selectedLocationIds.indexOf(location.id) > -1) {
          return true;
        }
      }
    }

    if (activeLocationTypes.indexOf(Locations.CITY) > -1 && location.type === Locations.DISTRICT) {
      const selectedLocationIds = selectedLocations.map((s) => s.id);
      if (selectedLocationIds.indexOf(location.city.id) > -1) {
        return true;
      }
    }

    if (activeLocationTypes.indexOf(Locations.REGION) > -1 && location.type === Locations.CITY) {
      const hasCityIds = selectedLocations?.[0]?.cityIds;
      let selectedLocationIds = [];
      if (hasCityIds) {
        selectedLocationIds = selectedLocations?.[0]?.cityIds;
      } else {
        selectedLocationIds = regionCityIds[selectedLocations?.[0]?.id];
      }
      if (selectedLocationIds?.indexOf(location?.city?.id) > -1) {
        return true;
      }
    }
  }

  return false;
};

const parentLocationTypes = {
  [Locations.LOCALITY]: [Locations.DISTRICT, Locations.CITY, Locations.REGION],
  [Locations.TOWN]: [Locations.DISTRICT, Locations.CITY, Locations.REGION],
  [Locations.DISTRICT]: [Locations.CITY, Locations.REGION],
  [Locations.CITY]: [Locations.REGION],
};

const getIds = (parentTypes, selectedLocations) => {
  const allIds = [];
  selectedLocations.forEach((selectedLocation) => {
    parentTypes.forEach((parentType) => {
      if (selectedLocation[parentType]) {
        allIds.push(selectedLocation[parentType].id);
      } else {
        allIds.push(selectedLocation.id);
      }
    });
  });
  return [...new Set(allIds)];
};

export const isSelectedLocationsParent = (location, selectedLocations, activeLocationTypes) => {
  const hasLocality = activeLocationTypes.includes(Locations.LOCALITY);
  const hasTown = activeLocationTypes.includes(Locations.TOWN);
  const hasDistrict = activeLocationTypes.includes(Locations.DISTRICT);
  const hasCity = activeLocationTypes.includes(Locations.CITY);

  if (hasLocality) {
    const parentTypes = parentLocationTypes[Locations.LOCALITY];
    return selectedLocations.some(
      (selectedLocation) => parentTypes.includes(location.type) && selectedLocation.id === location.id
    );
  }

  if (hasTown) {
    const parentTypes = parentLocationTypes[Locations.TOWN];
    const selectedLocationIds = getIds(parentTypes, selectedLocations);
    return selectedLocationIds.indexOf(location.id) > -1;
  }

  if (hasDistrict) {
    const parentTypes = parentLocationTypes[Locations.TOWN];
    const selectedLocationIds = getIds(parentTypes, selectedLocations);
    return selectedLocationIds.indexOf(location.id) > -1;
  }

  if (hasCity) {
    const parentTypes = parentLocationTypes[Locations.TOWN];
    const selectedLocationIds = getIds(parentTypes, selectedLocations);
    return selectedLocationIds.indexOf(location.id) > -1;
  }

  if (selectedLocations?.length === 1) {
    const selectedLocationIds = selectedLocations.map((s) => s.id);
    if (selectedLocationIds.indexOf(location.id) > -1) {
      return true;
    }
  }

  return false;
};

export const averageGeolocation = (coords) => {
  if (coords.length === 1) {
    return coords[0];
  }

  let x = 0.0;
  let y = 0.0;
  let z = 0.0;

  for (const coord of coords) {
    const latitude = (coord.latitude * Math.PI) / 180;
    const longitude = (coord.longitude * Math.PI) / 180;

    x += Math.cos(latitude) * Math.cos(longitude);
    y += Math.cos(latitude) * Math.sin(longitude);
    z += Math.sin(latitude);
  }

  const total = coords.length;

  x = x / total;
  y = y / total;
  z = z / total;

  const centralLongitude = Math.atan2(y, x);
  const centralSquareRoot = Math.sqrt(x * x + y * y);
  const centralLatitude = Math.atan2(z, centralSquareRoot);

  return {
    latitude: (centralLatitude * 180) / Math.PI,
    longitude: (centralLongitude * 180) / Math.PI,
  };
};

export const decidePOILocationToFocus = (locations) => {
  if (locations.length === 1) {
    return locations[0].town.id;
  }

  let towns = locations.map((location) => location.town.id);
  towns = this.reduceDuplicates(towns);
  if (towns.length === 1) {
    return towns[0];
  }

  let localities = locations.map((location) => location.locality.id);
  localities = this.reduceDuplicates(localities);
  if (localities.length === 1) {
    return localities[0];
  }

  let districts = locations.map((location) => location.district.id);
  districts = this.reduceDuplicates(districts);
  if (districts.length === 1) {
    return districts[0];
  }

  let cities = locations.map((location) => location.city.id);
  cities = this.reduceDuplicates(cities);
  if (cities.length === 1) {
    return cities[0];
  }
};

export const reduceDuplicates = (array) => {
  return array.reduce((unique, item) => (unique.includes(item) ? unique : [...unique, item]), []);
};

export const isLocationHasPOI = (searchCriteria) => {
  const locations = getNodeLocations(searchCriteria);

  if (locations && locations.length > 0) {
    const pois = locations.filter((location) => location.type === "poi");
    if (pois.length > 0) {
      return true;
    }
  }
  return false;
};

export const findParentLocations = (searchedLocations, locationTypes, childLocationType) => {
  const locationsToRemove = [];
  if (locationTypes.indexOf(childLocationType) > -1) {
    /* Find index(es) of childLocationType */
    let index = locationTypes.indexOf(childLocationType);
    const childLocationIndexes = [];
    while (index !== -1) {
      childLocationIndexes.push(index);
      index = locationTypes.indexOf(childLocationType, index + 1);
    }

    childLocationIndexes.forEach((childLocationIndex) => {
      searchedLocations.forEach((location) => {
        if (
          typeof location.parentId !== "undefined" &&
          location.parentId === searchedLocations[childLocationIndex].parentId
        ) {
          locationsToRemove.push(location.parentId);
        }
      });
    });
  }
  return locationsToRemove;
};

/** Removes parent locations and returns lowest level locations */
export const removeParentLocations = (searchedLocations) => {
  const locationTypes = searchedLocations.map((l) => l.type);
  const districtsToRemove = findParentLocations(searchedLocations, locationTypes, "town");
  const citiesToRemove = findParentLocations(searchedLocations, locationTypes, "district");
  const locationsToRemove = [...districtsToRemove, ...citiesToRemove];
  return searchedLocations.filter((l) => locationsToRemove.indexOf(l.id) === -1);
};

/** Removes locations which stands only as POIs parent location */
export const removePOIsParentLocations = (criteriaLocations) => {
  const result = {};

  /* Group locations by type */
  const groupedLocations = locationHelper.collectLocationsByType(criteriaLocations);

  const poiTownIds = groupedLocations?.poi?.map((poi) => poi?.town?.id);
  const poiDistrictIds = groupedLocations?.poi?.map((poi) => poi?.district?.id);
  const poiCityIds = groupedLocations?.poi?.map((poi) => poi?.city?.id);

  /* Check if location(s) are added as POIs parent and filter */
  if (typeof groupedLocations.town !== "undefined") {
    const freeTowns = groupedLocations.town.filter((town) => poiTownIds.indexOf(town.id) === -1);
    result.town = freeTowns;
  }

  if (typeof groupedLocations.district !== "undefined") {
    const freeDistricts = groupedLocations.district.filter((district) => poiDistrictIds.indexOf(district.id) === -1);
    result.district = freeDistricts;
  }

  if (typeof groupedLocations.city !== "undefined") {
    const freeCities = groupedLocations.city.filter((city) => poiCityIds.indexOf(city.id) === -1);
    if (freeCities.length > 0) result.city = freeCities;
  }

  result.poi = [...groupedLocations.poi];

  return result;
};

/** Splits POIs and Other Locations */
export const groupPOIsAndOtherLocations = (groupedLocations) => {
  const locations = {
    locations: [],
    pois: [...groupedLocations.poi],
  };
  if (typeof groupedLocations.town !== "undefined") {
    locations.locations = [...groupedLocations.town];
  }
  if (typeof groupedLocations.district !== "undefined") {
    locations.locations = [...groupedLocations.district];
  }
  if (typeof groupedLocations.city !== "undefined") {
    locations.locations = [...groupedLocations.city];
  }
  return locations;
};
